import type { HotkeyStorage } from "../types";

const STORAGE_PREFIX = "capitalrx-hotkeys:";

export class LocalStorageAdapter implements HotkeyStorage {
  private prefix: string;

  private fullKeyCache = new Map<string, string>();
  private getAllKeysCache: string[] | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseCache = new Map<string, any>();
  private lastLocalStorageLength: number = -1;

  constructor(prefix: string = STORAGE_PREFIX) {
    this.prefix = prefix;
  }

  private invalidateCaches(): void {
    this.getAllKeysCache = null;
    this.parseCache.clear();
    this.lastLocalStorageLength = localStorage.length;
  }

  //* Invalidate caches when localStorage length changes (indicates external modification)
  private checkStorageChanges(): void {
    if (this.lastLocalStorageLength !== localStorage.length) {
      this.invalidateCaches();
    }
  }

  get<T>(key: string): T | null {
    try {
      const fullKey = this.getFullKey(key);

      if (this.parseCache.has(fullKey)) {
        return this.parseCache.get(fullKey) as T;
      }

      const value = localStorage.getItem(fullKey);

      if (value === null) {
        return null;
      }

      const parsed = JSON.parse(value) as T;

      this.parseCache.set(fullKey, parsed);
      return parsed;
    } catch (error) {
      console.error(`Failed to get item from storage: ${key}`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      const fullKey = this.getFullKey(key);
      const serialized = JSON.stringify(value);
      localStorage.setItem(fullKey, serialized);

      this.parseCache.set(fullKey, value);
      this.invalidateCaches();
    } catch (error) {
      console.error(`Failed to set item in storage: ${key}`, error);

      if (error instanceof Error && error.name === "QuotaExceededError") {
        console.warn("LocalStorage quota exceeded. Consider clearing old data.");
      }
    }
  }

  remove(key: string): void {
    try {
      const fullKey = this.getFullKey(key);
      localStorage.removeItem(fullKey);

      this.parseCache.delete(fullKey);
      this.invalidateCaches();
    } catch (error) {
      console.error(`Failed to remove item from storage: ${key}`, error);
    }
  }

  clear(): void {
    try {
      const keys = this.getAllKeys();
      keys.forEach((key) => localStorage.removeItem(key));
      this.invalidateCaches();
    } catch (error) {
      console.error("Failed to clear storage", error);
    }
  }

  private getFullKey(key: string): string {
    if (this.fullKeyCache.has(key)) {
      return this.fullKeyCache.get(key)!;
    }

    const fullKey = `${this.prefix}${key}`;
    this.fullKeyCache.set(key, fullKey);
    return fullKey;
  }

  private getAllKeys(): string[] {
    this.checkStorageChanges();

    if (this.getAllKeysCache !== null) {
      return this.getAllKeysCache;
    }

    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keys.push(key);
      }
    }

    this.getAllKeysCache = keys;
    this.lastLocalStorageLength = localStorage.length;
    return keys;
  }

  clearCaches(): void {
    this.fullKeyCache.clear();
    this.getAllKeysCache = null;
    this.parseCache.clear();
    this.lastLocalStorageLength = -1;
  }

  getCacheStats() {
    return {
      fullKeyCache: this.fullKeyCache.size,
      parseCache: this.parseCache.size,
      hasGetAllKeysCache: this.getAllKeysCache !== null,
      getAllKeysCacheSize: this.getAllKeysCache?.length ?? 0,
      lastLocalStorageLength: this.lastLocalStorageLength,
    };
  }
}

export class MemoryStorageAdapter implements HotkeyStorage {
  private storage: Map<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseCache = new Map<string, any>();

  constructor() {
    this.storage = new Map();
  }

  get<T>(key: string): T | null {
    try {
      if (this.parseCache.has(key)) {
        return this.parseCache.get(key) as T;
      }

      const value = this.storage.get(key);

      if (value === undefined) {
        return null;
      }

      const parsed = JSON.parse(value) as T;

      this.parseCache.set(key, parsed);
      return parsed;
    } catch (error) {
      console.error(`Failed to get item from memory storage: ${key}`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      this.storage.set(key, serialized);

      this.parseCache.set(key, value);
    } catch (error) {
      console.error(`Failed to set item in memory storage: ${key}`, error);
    }
  }

  remove(key: string): void {
    this.storage.delete(key);
    this.parseCache.delete(key);
  }

  clear(): void {
    this.storage.clear();
    this.parseCache.clear();
  }

  clearCaches(): void {
    this.parseCache.clear();
  }

  getCacheStats() {
    return {
      storageSize: this.storage.size,
      parseCacheSize: this.parseCache.size,
    };
  }
}

let storageAvailabilityCache: boolean | null = null;

function testLocalStorageAvailability(): boolean {
  if (storageAvailabilityCache !== null) {
    return storageAvailabilityCache;
  }

  if (typeof window === "undefined" || !window.localStorage) {
    storageAvailabilityCache = false;
    return false;
  }

  try {
    const testKey = "__capitalrx_hotkeys_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    storageAvailabilityCache = true;
    return true;
  } catch (error) {
    console.warn("localStorage not available, using memory storage", error);
    storageAvailabilityCache = false;
    return false;
  }
}

export function createStorage(prefix?: string): HotkeyStorage {
  if (testLocalStorageAvailability()) {
    return new LocalStorageAdapter(prefix);
  }

  return new MemoryStorageAdapter();
}

export function clearStorageAvailabilityCache(): void {
  storageAvailabilityCache = null;
}

export function clearStorageCaches(storage: LocalStorageAdapter | MemoryStorageAdapter): void {
  storage.clearCaches();
}

export function getStorageCacheStats(storage: LocalStorageAdapter | MemoryStorageAdapter) {
  return storage.getCacheStats();
}
